import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { TextFormViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contentService } from '@/services/content.service'
import { EMPTY_TEXT_FORM, type TextFormValues, textCreateBody, textCreateErrors } from './text-form'
import { contentKeys, useContentLocales, useContentT, useContentTypes } from './useContentQueries'

/** `/content/texts/new` (TO-420 content-admin-ui REQ-003): schema-checked create, then open the text. */
export function useTextCreate(): TextFormViewProps {
	const { t, fieldError } = useContentT()
	const navigate = useNavigate()
	const client = useQueryClient()
	const types = useContentTypes()
	const [values, setValues] = useState<TextFormValues>(EMPTY_TEXT_FORM)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [formError, setFormError] = useState<string | null>(null)
	const type = types.data?.find((x) => x.code === values.typeCode)
	const { locales } = useContentLocales(type?.requiredLocales)
	const create = useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.createText(body),
		onSuccess: async (text) => {
			await client.invalidateQueries({ queryKey: contentKeys.texts })
			await navigate(`/content/texts/${text.id}`)
		},
		onError: (error) => {
			const { field, message } = fieldError(error)
			if (field === 'form') setFormError(message)
			else setErrors({ [field]: message })
		},
	})
	return {
		t,
		values,
		types: (types.data ?? []).map((x) => ({ id: x.code, label: x.name })),
		locales,
		errors,
		formError,
		isBusy: create.isPending,
		onChange: (field, value) => setValues((v) => ({ ...v, [field]: value })),
		onSubmit: () => {
			setFormError(null)
			const body = textCreateBody(values)
			const found = textCreateErrors(body)
			setErrors(Object.fromEntries(Object.entries(found).map(([f, key]) => [f, t(key)])))
			if (Object.keys(found).length === 0) create.mutate(body)
		},
	}
}
